import React, { useState } from 'react';
import { Globe, Zap, ShieldCheck, ArrowRightLeft, CheckCircle2, Wallet, TrendingUp, CreditCard } from './Icons';
import { Language, LandingViewState } from '../types';
import { translations } from '../utils/translations';

interface LandingPageProps {
  onLogin: () => void;
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLogin, language, setLanguage }) => {
  const [view, setView] = useState<LandingViewState>(LandingViewState.HOME);
  const t = translations[language];

  // --- Sub-components for different views ---

  const HomeView = () => (
    <>
    <div className="relative overflow-hidden pt-16 pb-24 lg:pt-32 lg:pb-40">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[600px] h-[600px] bg-brand-lilac/30 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[500px] h-[500px] bg-brand-lime/20 rounded-full blur-3xl opacity-50"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 text-center lg:text-left">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lilac/20 text-brand-purple text-sm font-semibold mb-6 border border-brand-lilac/50">
                <span className="flex h-2 w-2 rounded-full bg-brand-purple mr-2"></span>
                {t.hero.new}
              </div>
              <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight text-brand-dark mb-6 leading-[1.1]">
                {t.hero.titleStart} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-purple">{t.hero.titleEnd}</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {t.hero.desc}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={() => setView(LandingViewState.SIGNUP)}
                  className="bg-brand-blue hover:bg-brand-dark text-white text-lg px-8 py-4 rounded-full font-bold transition-all shadow-xl shadow-blue-500/20"
                >
                  {t.hero.openAccount}
                </button>
                <button 
                  onClick={() => setView(LandingViewState.ENTERPRISE)}
                  className="bg-white text-brand-dark border border-gray-200 hover:bg-gray-50 text-lg px-8 py-4 rounded-full font-bold transition-all"
                >
                  {t.hero.contactSales}
                </button>
              </div>
              <div className="mt-10 flex items-center justify-center lg:justify-start gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" /> {t.hero.noFees}</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" /> {t.hero.instant}</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" /> {t.hero.fdic}</div>
              </div>
            </div>
            
            {/* Hero Visual */}
            <div className="lg:col-span-6 mt-16 lg:mt-0 relative">
              <div className="relative rounded-2xl bg-gradient-to-br from-brand-dark to-brand-blue p-4 shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="absolute inset-0 bg-white/5 rounded-2xl backdrop-blur-sm"></div>
                <div className="bg-white rounded-xl p-6 shadow-inner relative z-10">
                  <div className="flex justify-between items-center mb-8">
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Total Balance</p>
                      <h3 className="text-3xl font-bold text-slate-900">$142,304.00</h3>
                    </div>
                    <div className="w-12 h-12 bg-brand-green/20 rounded-full flex items-center justify-center text-brand-green">
                      <Zap className="w-6 h-6 fill-current" />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-brand-lilac rounded-full flex items-center justify-center text-brand-purple font-bold">S</div>
                        <div>
                          <p className="font-bold text-slate-900">Stripe Payout</p>
                          <p className="text-xs text-slate-500">Today, 10:23 AM</p>
                        </div>
                      </div>
                      <span className="text-brand-green font-bold">+ $4,250.00</span>
                    </div>
                    
                     <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-brand-blue font-bold">W</div>
                        <div>
                          <p className="font-bold text-slate-900">Web Hosting Inc</p>
                          <p className="text-xs text-slate-500">Yesterday</p>
                        </div>
                      </div>
                      <span className="text-slate-900 font-bold">- $120.00</span>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-gray-100 flex justify-between">
                    <button className="flex-1 bg-brand-dark text-white py-3 rounded-lg font-medium mr-2">Send Money</button>
                    <button className="flex-1 bg-brand-green text-brand-dark py-3 rounded-lg font-bold ml-2">Add Funds</button>
                  </div>
                </div>
              </div>
              
              {/* Floating Element */}
              <div className="absolute -bottom-10 -left-10 bg-white p-4 rounded-xl shadow-xl border border-brand-lilac animate-bounce-slow">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-semibold">Exchange Rate</p>
                    <p className="text-sm font-bold text-slate-900">1 USD = 0.92 EUR</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-brand-dark mb-4">{t.features.title}</h2>
            <p className="text-lg text-slate-600">{t.features.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue mb-6">
                <Globe className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3">{t.features.f1_title}</h3>
              <p className="text-slate-600">{t.features.f1_desc}</p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 bg-brand-lilac/30 rounded-xl flex items-center justify-center text-brand-purple mb-6">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3">{t.features.f2_title}</h3>
              <p className="text-slate-600">{t.features.f2_desc}</p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 bg-brand-lime/30 rounded-xl flex items-center justify-center text-green-700 mb-6">
                <ArrowRightLeft className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3">{t.features.f3_title}</h3>
              <p className="text-slate-600">{t.features.f3_desc}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const ProductView = () => (
    <div className="pt-24 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-extrabold text-brand-dark mb-4">{t.product.title}</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">{t.product.subtitle}</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Globe className="w-8 h-8 text-brand-blue" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">{t.product.f1_title}</h3>
            <p className="text-slate-600">{t.product.f1_desc}</p>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="bg-purple-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <CreditCard className="w-8 h-8 text-brand-purple" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">{t.product.f2_title}</h3>
            <p className="text-slate-600">{t.product.f2_desc}</p>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-brand-green" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">{t.product.f3_title}</h3>
            <p className="text-slate-600">{t.product.f3_desc}</p>
        </div>
      </div>
    </div>
  );

  const PricingView = () => (
     <div className="pt-24 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-extrabold text-brand-dark mb-4">{t.pricing.title}</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">{t.pricing.subtitle}</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {/* Starter */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-2">{t.pricing.free}</h3>
            <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-brand-dark">{t.pricing.free_price}</span>
                <span className="text-slate-500 ml-2">{t.pricing.mo}</span>
            </div>
            <ul className="space-y-4 mb-8">
                {t.pricing.perks_free.map((perk, i) => (
                    <li key={i} className="flex items-center text-slate-600">
                        <CheckCircle2 className="w-5 h-5 text-brand-green mr-3" /> {perk}
                    </li>
                ))}
            </ul>
            <button onClick={() => setView(LandingViewState.SIGNUP)} className="w-full bg-slate-100 text-slate-900 font-bold py-3 rounded-xl hover:bg-slate-200 transition-colors">
                {t.nav.getStarted}
            </button>
        </div>
        
        {/* Pro */}
        <div className="bg-brand-dark rounded-2xl shadow-xl border border-brand-blue p-8 relative transform md:-translate-y-4">
             <div className="absolute top-0 right-0 bg-brand-green text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg text-brand-dark">POPULAR</div>
            <h3 className="text-xl font-bold text-white mb-2">{t.pricing.pro}</h3>
            <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-white">{t.pricing.pro_price}</span>
                <span className="text-slate-400 ml-2">{t.pricing.mo}</span>
            </div>
            <ul className="space-y-4 mb-8">
                 {t.pricing.perks_pro.map((perk, i) => (
                    <li key={i} className="flex items-center text-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-brand-lime mr-3" /> {perk}
                    </li>
                ))}
            </ul>
            <button onClick={() => setView(LandingViewState.SIGNUP)} className="w-full bg-brand-blue text-white font-bold py-3 rounded-xl hover:bg-blue-600 transition-colors shadow-lg">
                {t.nav.getStarted}
            </button>
        </div>

        {/* Enterprise */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-2">{t.pricing.ent}</h3>
            <div className="flex items-baseline mb-6">
                <span className="text-4xl font-extrabold text-brand-dark">{t.pricing.ent_price}</span>
            </div>
            <ul className="space-y-4 mb-8">
                 {t.pricing.perks_ent.map((perk, i) => (
                    <li key={i} className="flex items-center text-slate-600">
                        <CheckCircle2 className="w-5 h-5 text-brand-green mr-3" /> {perk}
                    </li>
                ))}
            </ul>
            <button onClick={() => setView(LandingViewState.ENTERPRISE)} className="w-full bg-white border border-brand-dark text-brand-dark font-bold py-3 rounded-xl hover:bg-gray-50 transition-colors">
                {t.hero.contactSales}
            </button>
        </div>
      </div>
     </div>
  );

  const EnterpriseView = () => (
     <div className="pt-24 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-brand-dark rounded-3xl overflow-hidden shadow-2xl relative">
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[600px] h-[600px] bg-brand-blue/30 rounded-full blur-3xl opacity-50"></div>
            <div className="relative z-10 p-12 lg:p-24 text-center">
                <h1 className="text-4xl lg:text-6xl font-extrabold text-white mb-6">{t.enterprise.title}</h1>
                <p className="text-xl text-slate-300 mb-10 max-w-3xl mx-auto">{t.enterprise.subtitle}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button className="bg-brand-green text-brand-dark text-lg px-8 py-4 rounded-full font-bold hover:bg-green-400 transition-colors">
                        {t.enterprise.cta}
                    </button>
                    <button className="bg-white/10 backdrop-blur text-white border border-white/20 text-lg px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-colors">
                        Read Case Studies
                    </button>
                </div>
            </div>
        </div>
        <div className="mt-20 text-center">
             <p className="text-slate-500 mb-6">Trusted by global platforms</p>
             <div className="flex justify-center gap-12 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                {/* Simple generic logos */}
                <div className="text-2xl font-bold font-serif text-slate-700">ACME Corp</div>
                <div className="text-2xl font-bold font-mono text-slate-700">Globex</div>
                <div className="text-2xl font-bold font-sans text-slate-700 italic">Soylent</div>
                <div className="text-2xl font-bold text-slate-700">Umbrella</div>
             </div>
        </div>
     </div>
  );

  const AuthForm = ({ type }: { type: 'login' | 'signup' }) => (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-2xl shadow-xl border border-gray-100">
            <div className="text-center">
                <div className="mx-auto h-12 w-12 bg-brand-blue rounded-xl flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-2xl">B</span>
                </div>
                <h2 className="text-3xl font-extrabold text-slate-900">
                    {type === 'login' ? t.auth.login_title : t.auth.signup_title}
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                    {type === 'login' ? t.auth.login_subtitle : t.auth.signup_subtitle}
                </p>
            </div>
            <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); onLogin(); }}>
                <div className="space-y-4">
                    {type === 'signup' && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t.auth.name}</label>
                            <input type="text" required className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-brand-blue focus:border-brand-blue sm:text-sm" />
                        </div>
                    )}
                    <div>
                         <label className="block text-sm font-medium text-slate-700 mb-1">{t.auth.email}</label>
                        <input type="email" required className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-brand-blue focus:border-brand-blue sm:text-sm" />
                    </div>
                    {type === 'login' && (
                        <div>
                             <label className="block text-sm font-medium text-slate-700 mb-1">{t.auth.password}</label>
                            <input type="password" required className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-brand-blue focus:border-brand-blue sm:text-sm" />
                        </div>
                    )}
                </div>

                <button type="submit" className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-brand-blue hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue shadow-lg shadow-blue-500/30 transition-all">
                    {type === 'login' ? t.auth.submit_login : t.auth.submit_signup}
                </button>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">{t.auth.or}</span></div>
                </div>

                <button type="button" className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-xl shadow-sm bg-white text-sm font-medium text-slate-700 hover:bg-gray-50">
                    <svg className="h-5 w-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    {t.auth.google}
                </button>
            </form>
        </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
                className="flex items-center gap-2 cursor-pointer" 
                onClick={() => setView(LandingViewState.HOME)}
            >
              <div className="w-8 h-8 bg-brand-blue rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <span className="text-2xl font-bold tracking-tight text-brand-dark">Banco<span className="text-brand-blue">WEB</span></span>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => setView(LandingViewState.PRODUCT)} className={`font-medium transition-colors ${view === LandingViewState.PRODUCT ? 'text-brand-blue' : 'text-slate-600 hover:text-brand-blue'}`}>{t.nav.product}</button>
              <button onClick={() => setView(LandingViewState.PRICING)} className={`font-medium transition-colors ${view === LandingViewState.PRICING ? 'text-brand-blue' : 'text-slate-600 hover:text-brand-blue'}`}>{t.nav.pricing}</button>
              <button onClick={() => setView(LandingViewState.ENTERPRISE)} className={`font-medium transition-colors ${view === LandingViewState.ENTERPRISE ? 'text-brand-blue' : 'text-slate-600 hover:text-brand-blue'}`}>{t.nav.enterprise}</button>
            </div>
            <div className="flex items-center">
              {/* Language Switcher */}
              <div className="flex items-center bg-gray-100 rounded-full p-1 mr-4">
                {(['EN', 'FR', 'ES'] as Language[]).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setLanguage(lang)}
                    className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                      language === lang 
                        ? 'bg-white text-brand-blue shadow-sm' 
                        : 'text-slate-500 hover:text-brand-blue'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>

              <button onClick={() => setView(LandingViewState.LOGIN)} className="text-slate-900 font-semibold hover:text-brand-blue mr-4">{t.nav.login}</button>
              <button 
                onClick={() => setView(LandingViewState.SIGNUP)}
                className="bg-brand-green hover:bg-green-400 text-brand-dark px-5 py-2.5 rounded-full font-bold transition-all shadow-lg hover:shadow-green-200/50"
              >
                {t.nav.getStarted}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Dynamic View Content */}
      <div className="animate-fade-in">
        {view === LandingViewState.HOME && <HomeView />}
        {view === LandingViewState.PRODUCT && <ProductView />}
        {view === LandingViewState.PRICING && <PricingView />}
        {view === LandingViewState.ENTERPRISE && <EnterpriseView />}
        {view === LandingViewState.LOGIN && <AuthForm type="login" />}
        {view === LandingViewState.SIGNUP && <AuthForm type="signup" />}
      </div>

    </div>
  );
};

export default LandingPage;